import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Alert,
  List,
  ListItem,
  ListItemText,
  IconButton,
  LinearProgress,
  Avatar,
  Tooltip,
  Fade,
  Zoom,
  useTheme,
  alpha,
} from '@mui/material';
import {
  ExpandMore,
  CheckCircle,
  RadioButtonUnchecked,
  Schedule,
  Edit,
  Science,
  Timeline,
  Build,
  Analytics,
  AutoAwesome,
  Bolt,
} from '@mui/icons-material';
import { Sample } from '../services/samplesService';

interface ProgressiveWorkflowDisplayProps {
  sample: Sample;
  onEditPreparation?: () => void;
  onEditAnalysis?: () => void;
  showEditButtons?: boolean;
}

const ProgressiveWorkflowDisplay: React.FC<ProgressiveWorkflowDisplayProps> = ({
  sample,
  onEditPreparation,
  onEditAnalysis,
  showEditButtons = true,
}) => {
  const theme = useTheme();
  
  const getPreparationStatus = (metadata: any) => {
    if (!metadata?.preparation_status) return 'pending';
    
    switch (metadata.preparation_status) {
      case 'SAVE_DRAFT': return 'draft';
      case 'START_PREPARATION': return 'in_progress';
      case 'COMPLETE_PREPARATION': return 'completed';
      default: return 'pending';
    }
  };

  const getAnalysisStatus = (metadata: any) => {
    if (!metadata?.analysis_status) return 'pending';
    
    switch (metadata.analysis_status) {
      case 'SAVE_DRAFT': return 'draft';
      case 'SAVE_RESULTS': return 'in_progress';
      case 'FINALIZE_ANALYSIS': return 'completed';
      default: return 'pending';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed': return <CheckCircle color="success" />;
      case 'in_progress': return <Schedule color="primary" />;
      case 'draft': return <Edit color="warning" />;
      case 'pending': return <RadioButtonUnchecked color="disabled" />;
      default: return <RadioButtonUnchecked color="disabled" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return theme.palette.success.main;
      case 'in_progress': return theme.palette.primary.main;
      case 'draft': return theme.palette.warning.main;
      case 'pending': return theme.palette.grey[400];
      default: return theme.palette.grey[400];
    }
  };

  const getStatusGradient = (status: string) => {
    switch (status) {
      case 'completed': return `linear-gradient(135deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`;
      case 'in_progress': return `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`;
      case 'draft': return `linear-gradient(135deg, ${theme.palette.warning.main}, ${theme.palette.warning.dark})`;
      case 'pending': return `linear-gradient(135deg, ${theme.palette.grey[400]}, ${theme.palette.grey[600]})`;
      default: return `linear-gradient(135deg, ${theme.palette.grey[400]}, ${theme.palette.grey[600]})`;
    }
  };

  const getProgressPercentage = (prepStatus: string, analysisStatus: string) => {
    let progress = 0;
    
    // Preparation progress (50% of total)
    switch (prepStatus) {
      case 'draft': progress += 15; break;
      case 'in_progress': progress += 30; break;
      case 'completed': progress += 50; break;
    }
    
    // Analysis progress (50% of total)
    switch (analysisStatus) {
      case 'draft': progress += 15; break;
      case 'in_progress': progress += 30; break;
      case 'completed': progress += 50; break;
    }
    
    return Math.min(progress, 100);
  };

  const metadata = sample.metadonnees || {};
  const prepStatus = getPreparationStatus(metadata);
  const analysisStatus = getAnalysisStatus(metadata);
  const overallProgress = getProgressPercentage(prepStatus, analysisStatus);

  return (
    <Box>
      {/* Modern Header Card */}
      <Fade in timeout={600}>
        <Card sx={{ 
          mb: 3,
          borderRadius: 4,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.secondary.main, 0.1)})`,
          backdropFilter: 'blur(20px)',
          border: `1px solid ${alpha(theme.palette.common.white, 0.2)}`,
          overflow: 'visible',
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '4px',
            background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            borderRadius: '16px 16px 0 0',
          }
        }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ 
                  background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                  width: 48,
                  height: 48,
                }}>
                  <Timeline />
                </Avatar>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>
                    Progression du Workflow
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Suivi en temps réel des étapes
                  </Typography>
                </Box>
              </Box>
              <Chip 
                label={sample.statut} 
                color={sample.statut === 'PRET' ? 'success' : 'primary'}
                variant="filled"
                sx={{ 
                  fontWeight: 600,
                  px: 2,
                  background: sample.statut === 'PRET' 
                    ? `linear-gradient(45deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`
                    : `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                }}
              />
            </Box>

            {/* Overall Progress */}
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                  Progression globale
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                  {overallProgress}%
                </Typography>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={overallProgress}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  background: alpha(theme.palette.grey[300], 0.3),
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 4,
                    background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                  }
                }}
              />
            </Box>

            {/* Quick Stats */}
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Box sx={{ 
                  p: 2, 
                  borderRadius: 3,
                  background: alpha(theme.palette.primary.main, 0.1),
                  textAlign: 'center',
                }}>
                  <Build color="primary" sx={{ mb: 1 }} />
                  <Typography variant="caption" color="text.secondary" display="block">
                    Préparation
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: getStatusColor(prepStatus) }}>
                    {prepStatus === 'completed' ? 'Terminée' : 
                     prepStatus === 'in_progress' ? 'En cours' :
                     prepStatus === 'draft' ? 'Brouillon' : 'En attente'}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box sx={{ 
                  p: 2, 
                  borderRadius: 3,
                  background: alpha(theme.palette.secondary.main, 0.1),
                  textAlign: 'center',
                }}>
                  <Science color="secondary" sx={{ mb: 1 }} />
                  <Typography variant="caption" color="text.secondary" display="block">
                    Analyse
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: getStatusColor(analysisStatus) }}>
                    {analysisStatus === 'completed' ? 'Terminée' : 
                     analysisStatus === 'in_progress' ? 'En cours' :
                     analysisStatus === 'draft' ? 'Brouillon' : 'En attente'}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Fade>

      {/* Enhanced Preparation Section */}
      <Zoom in timeout={800}>
        <Accordion 
          defaultExpanded={prepStatus === 'in_progress' || prepStatus === 'draft'}
          sx={{
            mb: 2,
            borderRadius: 4,
            background: alpha(theme.palette.background.paper, 0.8),
            backdropFilter: 'blur(20px)',
            border: `1px solid ${alpha(theme.palette.common.white, 0.2)}`,
            overflow: 'hidden',
            '&::before': {
              display: 'none',
            },
            '&.Mui-expanded': {
              margin: '0 0 16px 0',
            }
          }}
        >
          <AccordionSummary 
            expandIcon={<ExpandMore />}
            sx={{
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.light, 0.1)})`,
              borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              '&:hover': {
                background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.15)}, ${alpha(theme.palette.primary.light, 0.15)})`,
              }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
              <Avatar sx={{ 
                background: getStatusGradient(prepStatus),
                width: 40,
                height: 40,
              }}>
                {getStatusIcon(prepStatus)}
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Préparation Mécanique
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Concassage, broyage et conditionnement
                </Typography>
              </Box>
              {metadata.preparation_status && (
                <Chip 
                  label={metadata.preparation_status}
                  size="small"
                  sx={{
                    fontWeight: 500,
                    background: getStatusGradient(prepStatus),
                    color: 'white',
                  }}
                />
              )}
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 3 }}>
            {metadata.preparation_data ? (
              <Box>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ 
                      p: 2, 
                      borderRadius: 3,
                      background: alpha(theme.palette.info.main, 0.1),
                      border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                    }}>
                      <Typography variant="subtitle2" color="info.main" sx={{ fontWeight: 600, mb: 1 }}>
                        👨‍🔬 Technicien
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {metadata.preparation_data.technician_id || 'Non assigné'}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ 
                      p: 2, 
                      borderRadius: 3,
                      background: alpha(theme.palette.success.main, 0.1),
                      border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                    }}>
                      <Typography variant="subtitle2" color="success.main" sx={{ fontWeight: 600, mb: 1 }}>
                        ⚖️ Poids initial
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {metadata.preparation_data.poids_initial_kg ? `${metadata.preparation_data.poids_initial_kg} kg` : 'Non défini'}
                      </Typography>
                    </Box>
                  </Grid>
                  {metadata.preparation_data.crushing_method && (
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ 
                        p: 2, 
                        borderRadius: 3,
                        background: alpha(theme.palette.warning.main, 0.1),
                        border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
                      }}>
                        <Typography variant="subtitle2" color="warning.main" sx={{ fontWeight: 600, mb: 1 }}>
                          🔨 Méthode de concassage
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {metadata.preparation_data.crushing_method}
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                  {metadata.preparation_data.equipment_ids && metadata.preparation_data.equipment_ids.length > 0 && (
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ 
                        p: 2, 
                        borderRadius: 3,
                        background: alpha(theme.palette.secondary.main, 0.1),
                        border: `1px solid ${alpha(theme.palette.secondary.main, 0.2)}`,
                      }}>
                        <Typography variant="subtitle2" color="secondary.main" sx={{ fontWeight: 600, mb: 1 }}>
                          🛠️ Équipements ({metadata.preparation_data.equipment_ids.length})
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {metadata.preparation_data.equipment_ids.slice(0, 2).map((equipId: string, index: number) => (
                            <Chip 
                              key={index}
                              label={equipId}
                              size="small"
                              variant="outlined"
                              sx={{ fontSize: '0.75rem' }}
                            />
                          ))}
                          {metadata.preparation_data.equipment_ids.length > 2 && (
                            <Chip 
                              label={`+${metadata.preparation_data.equipment_ids.length - 2}`}
                              size="small"
                              color="secondary"
                            />
                          )}
                        </Box>
                      </Box>
                    </Grid>
                  )}
                </Grid>
                {onEditPreparation && showEditButtons && (
                  <Box sx={{ mt: 3, textAlign: 'center' }}>
                    <Tooltip title="Modifier la préparation" arrow>
                      <IconButton 
                        color="primary" 
                        onClick={onEditPreparation}
                        sx={{
                          background: alpha(theme.palette.primary.main, 0.1),
                          '&:hover': {
                            background: alpha(theme.palette.primary.main, 0.2),
                            transform: 'scale(1.1)',
                          }
                        }}
                      >
                        <Edit />
                      </IconButton>
                    </Tooltip>
                  </Box>
                )}
              </Box>
            ) : (
              <Alert 
                severity="info"
                sx={{
                  borderRadius: 3,
                  background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.1)}, ${alpha(theme.palette.info.light, 0.1)})`,
                  border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                      Aucune donnée de préparation disponible
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Commencez la préparation pour voir les détails techniques
                    </Typography>
                  </Box>
                  {onEditPreparation && showEditButtons && (
                    <Tooltip title="Commencer la préparation" arrow>
                      <IconButton 
                        color="primary" 
                        onClick={onEditPreparation}
                        sx={{
                          background: alpha(theme.palette.primary.main, 0.1),
                          '&:hover': {
                            background: alpha(theme.palette.primary.main, 0.2),
                            transform: 'scale(1.1)',
                          }
                        }}
                      >
                        <AutoAwesome />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </Alert>
            )}
          </AccordionDetails>
        </Accordion>
      </Zoom>

      {/* Enhanced Analysis Section */}
      <Zoom in timeout={1000}>
        <Accordion 
          defaultExpanded={analysisStatus === 'in_progress' || analysisStatus === 'draft'}
          sx={{
            borderRadius: 4,
            background: alpha(theme.palette.background.paper, 0.8),
            backdropFilter: 'blur(20px)',
            border: `1px solid ${alpha(theme.palette.common.white, 0.2)}`,
            overflow: 'hidden',
            '&::before': {
              display: 'none',
            },
            '&.Mui-expanded': {
              margin: 0,
            }
          }}
        >
          <AccordionSummary 
            expandIcon={<ExpandMore />}
            sx={{
              background: `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.1)}, ${alpha(theme.palette.secondary.light, 0.1)})`,
              borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              '&:hover': {
                background: `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.15)}, ${alpha(theme.palette.secondary.light, 0.15)})`,
              }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
              <Avatar sx={{ 
                background: getStatusGradient(analysisStatus),
                width: 40,
                height: 40,
              }}>
                {getStatusIcon(analysisStatus)}
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Analyse Chimique
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Tests et mesures de composition
                </Typography>
              </Box>
              {metadata.analysis_status && (
                <Chip 
                  label={metadata.analysis_status}
                  size="small"
                  sx={{
                    fontWeight: 500,
                    background: getStatusGradient(analysisStatus),
                    color: 'white',
                  }}
                />
              )}
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 3 }}>
            {metadata.analysis_data ? (
              <Box>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ 
                      p: 2, 
                      borderRadius: 3,
                      background: alpha(theme.palette.info.main, 0.1),
                      border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                    }}>
                      <Typography variant="subtitle2" color="info.main" sx={{ fontWeight: 600, mb: 1 }}>
                        🧪 Analyste
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {metadata.analysis_data.analyst_id || 'Non assigné'}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ 
                      p: 2, 
                      borderRadius: 3,
                      background: alpha(theme.palette.success.main, 0.1),
                      border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                    }}>
                      <Typography variant="subtitle2" color="success.main" sx={{ fontWeight: 600, mb: 1 }}>
                        🔬 Méthode
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {metadata.analysis_data.analysis_method || 'Non définie'}
                      </Typography>
                    </Box>
                  </Grid>
                  {metadata.analysis_data.results && metadata.analysis_data.results.length > 0 && (
                    <Grid item xs={12}>
                      <Box sx={{ 
                        p: 2, 
                        borderRadius: 3,
                        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.secondary.main, 0.1)})`,
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                      }}>
                        <Typography variant="subtitle2" color="primary.main" sx={{ fontWeight: 600, mb: 2 }}>
                          📊 Résultats ({metadata.analysis_data.results.length} éléments)
                        </Typography>
                        <List dense sx={{ p: 0 }}>
                          {metadata.analysis_data.results.slice(0, 3).map((result: any, index: number) => (
                            <ListItem 
                              key={index}
                              sx={{ 
                                px: 0,
                                py: 0.5,
                                borderRadius: 2,
                                mb: 0.5,
                                background: alpha(theme.palette.background.paper, 0.5),
                              }}
                            >
                              <ListItemText 
                                primary={
                                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                      {result.element}
                                    </Typography>
                                    <Chip 
                                      label={`${result.value} ${result.unit}`}
                                      size="small"
                                      color="primary"
                                      variant="outlined"
                                    />
                                  </Box>
                                }
                              />
                            </ListItem>
                          ))}
                          {metadata.analysis_data.results.length > 3 && (
                            <ListItem sx={{ px: 0, py: 0.5 }}>
                              <ListItemText 
                                primary={
                                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                    ... et {metadata.analysis_data.results.length - 3} autres résultats
                                  </Typography>
                                }
                              />
                            </ListItem>
                          )}
                        </List>
                      </Box>
                    </Grid>
                  )}
                </Grid>
                {onEditAnalysis && showEditButtons && (
                  <Box sx={{ mt: 3, textAlign: 'center' }}>
                    <Tooltip title="Modifier l'analyse" arrow>
                      <IconButton 
                        color="secondary" 
                        onClick={onEditAnalysis}
                        sx={{
                          background: alpha(theme.palette.secondary.main, 0.1),
                          '&:hover': {
                            background: alpha(theme.palette.secondary.main, 0.2),
                            transform: 'scale(1.1)',
                          }
                        }}
                      >
                        <Analytics />
                      </IconButton>
                    </Tooltip>
                  </Box>
                )}
              </Box>
            ) : (
              <Alert 
                severity="info"
                sx={{
                  borderRadius: 3,
                  background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.1)}, ${alpha(theme.palette.info.light, 0.1)})`,
                  border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                      Aucune donnée d'analyse disponible
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Commencez l'analyse pour voir les résultats détaillés
                    </Typography>
                  </Box>
                  {onEditAnalysis && showEditButtons && (
                    <Tooltip title="Commencer l'analyse" arrow>
                      <IconButton 
                        color="secondary" 
                        onClick={onEditAnalysis}
                        sx={{
                          background: alpha(theme.palette.secondary.main, 0.1),
                          '&:hover': {
                            background: alpha(theme.palette.secondary.main, 0.2),
                            transform: 'scale(1.1)',
                          }
                        }}
                      >
                        <Bolt />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </Alert>
            )}
          </AccordionDetails>
        </Accordion>
      </Zoom>
    </Box>
  );
};

export default ProgressiveWorkflowDisplay; 